import React from 'react';

interface WpMediaResource {
  wpMediaId: number;
  title: string;
  sourceUrl: string;
  thumbnailUrl?: string;
  mimeType: string;
  mediaType: string;
  altText?: string;
}

interface ResourceGalleryProps {
  resources: WpMediaResource[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

const FileIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-10 h-10 text-gray-400"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
    />
  </svg>
);

const ResourceGallery: React.FC<ResourceGalleryProps> = ({ resources, loading, error, onRetry }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondaryStoryAdditionalLightBlueText"></div>
        <span className="ml-3 text-gray-500 text-sm">Loading resources...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <p className="text-gray-500 text-sm mb-2">Failed to load resources.</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-sm text-secondaryStoryAdditionalLightBlueText hover:underline"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!resources.length) {
    return (
      <p className="text-gray-500 text-sm py-4 text-center">
        No resources available for this project template.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
      {resources.map((resource) => {
        const isImage = resource.mediaType === 'image';
        return (
          <a
            key={resource.wpMediaId}
            href={resource.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center rounded-lg border border-gray-200 p-2 hover:border-secondaryStoryAdditionalLightBlueText hover:shadow-md transition-all"
            title={`Download: ${resource.title}`}
          >
            <div className="w-full h-24 flex items-center justify-center overflow-hidden rounded bg-gray-50">
              {resource.thumbnailUrl ? (
                <img
                  src={resource.thumbnailUrl}
                  alt={resource.altText || resource.title}
                  className="max-w-full max-h-full object-contain"
                />
              ) : isImage ? (
                <img
                  src={resource.sourceUrl}
                  alt={resource.altText || resource.title}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <FileIcon />
              )}
            </div>
            <span className="text-xs text-gray-700 mt-1.5 text-center line-clamp-2 group-hover:text-secondaryStoryAdditionalLightBlueText">
              {resource.title}
            </span>
          </a>
        );
      })}
    </div>
  );
};

export default ResourceGallery;
